/**
 * GeneratingStep.jsx
 * ==================
 * AI generation step - analyzes assets and generates pages
 * Happens after asset upload to use uploaded content for better generation
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { generateContent, generateAppJSON } from '../../services/openaiService';
import apiService from '../../services/apiService';
import SetupWizard from './SetupWizard';

const GeneratingStep = () => {
  const { appName } = useParams();
  const [searchParams] = useSearchParams();
  const appType = searchParams.get('appType') || 'webapp';
  
  const [status, setStatus] = useState('skipped'); // skipped (for now), analyzing, generating, saving, complete, error
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  const [error, setError] = useState(null);

  // Commented out auto-generation for now as requested by user
  // useEffect(() => {
  //   generatePages();
  // }, []);

  const generatePages = async () => {
    try {
      // Step 1: Analyze uploaded assets
      setStatus('analyzing');
      setCurrentTask('Analyzing your uploaded assets...');
      setProgress(10);
      
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate analysis
      
      // Step 2: Generate content strategy with OpenAI
      setStatus('generating');
      setCurrentTask('Creating content strategy with AI...');
      setProgress(30);
      
      const contentJSON = await generateContent(appName, `A ${appType} application`);
      setProgress(50);
      
      // Step 3: Generate full page JSON
      setCurrentTask('Generating pages and components...');
      const pagesJSON = await generateAppJSON(appName, `A ${appType} application`, contentJSON);
      setProgress(70);
      
      // Step 4: Save to database
      setStatus('saving');
      setCurrentTask('Saving your pages...');
      setProgress(80);
      
      if (pagesJSON && pagesJSON.length > 0) {
        for (const page of pagesJSON) {
          await apiService.savePage(appName, page.pageName || page.name || 'page', page);
        }
      }
      
      setProgress(100);
      setStatus('complete');
      setCurrentTask('All done! Click Continue to proceed.');
      
    } catch (err) {
      console.error('Generation error:', err);
      setStatus('error');
      setError(err.message || 'Failed to generate pages');
      setCurrentTask('Generation failed');
    }
  };

  const handleStartGeneration = () => {
    setStatus('analyzing');
    setProgress(0);
    setError(null);
    generatePages();
  };

  const handleRetry = () => {
    setStatus('analyzing');
    setProgress(0);
    setError(null);
    generatePages();
  };

  return (
    <SetupWizard currentStepKey="generating">
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        {status === 'skipped' ? (
          <>
            {/* Skipped State - For Now */}
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>✨</div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '16px'
            }}>
              AI Page Generation
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              This step will use AI to analyze your uploaded assets and generate 
              sophisticated pages for your application.
            </p>

            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #FCD34D',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#92400E',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ⚠️ Temporarily Skipped
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#78350F',
                margin: 0,
                lineHeight: '1.5'
              }}>
                OpenAI integration is currently being configured. 
                For now, click <strong>Continue</strong> to proceed with manual page setup. 
                You can still create beautiful pages in the next steps!
              </p>
            </div>

            <button
              onClick={handleStartGeneration}
              disabled
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#9CA3AF',
                border: 'none',
                borderRadius: '8px',
                cursor: 'not-allowed',
                marginBottom: '16px'
              }}
            >
              🤖 Generate with AI (Coming Soon)
            </button>

            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              fontStyle: 'italic'
            }}>
              Click "Continue" below to move to the next step
            </div>
          </>
        ) : status === 'error' ? (
          <>
            {/* Error State */}
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>❌</div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#DC2626',
              marginBottom: '16px'
            }}>
              Generation Failed
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              marginBottom: '32px'
            }}>
              {error}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleRetry}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#3B82F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                🔄 Retry
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Generating State */}
            <div style={{
              fontSize: '64px',
              marginBottom: '24px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              {status === 'analyzing' ? '🔍' : 
               status === 'generating' ? '✨' : 
               status === 'saving' ? '💾' : '✅'}
            </div>

            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '16px'
            }}>
              {status === 'complete' ? 'Pages Generated!' : 'Generating Your App...'}
            </h2>

            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              marginBottom: '40px'
            }}>
              {currentTask}
            </p>

            {/* Progress Bar */}
            <div style={{
              width: '100%',
              maxWidth: '500px',
              margin: '0 auto 24px',
              backgroundColor: '#E5E7EB',
              borderRadius: '8px',
              height: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: appType === 'static' ? '#10B981' : 
                                appType === 'webapp' ? '#3B82F6' : '#EC4899',
                transition: 'width 0.5s ease',
                borderRadius: '8px'
              }} />
            </div>

            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '32px'
            }}>
              {progress}%
            </div>

            {/* What's Happening */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              padding: '24px',
              textAlign: 'left'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '16px'
              }}>
                What's happening:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{progress >= 10 ? '✅' : '⏳'}</span>
                  <span style={{ fontSize: '14px', color: progress >= 10 ? '#10B981' : '#6B7280' }}>
                    Analyzing uploaded assets
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{progress >= 30 ? '✅' : '⏳'}</span>
                  <span style={{ fontSize: '14px', color: progress >= 30 ? '#10B981' : '#6B7280' }}>
                    Generating content with AI
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{progress >= 50 ? '✅' : '⏳'}</span>
                  <span style={{ fontSize: '14px', color: progress >= 50 ? '#10B981' : '#6B7280' }}>
                    Creating pages and components
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{progress >= 80 ? '✅' : '⏳'}</span>
                  <span style={{ fontSize: '14px', color: progress >= 80 ? '#10B981' : '#6B7280' }}>
                    Saving to your app
                  </span>
                </div>
              </div>
            </div>

            {/* Info: Use Continue button at bottom to proceed */}
            {status === 'complete' && (
              <div style={{
                marginTop: '24px',
                fontSize: '14px',
                color: '#10B981',
                fontWeight: '600'
              }}>
                ✅ Click "Continue" below to proceed to the next step
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </SetupWizard>
  );
};

export default GeneratingStep;

